<template>
  <div>
    <b-container class="py-3">
      <b-row>
        <b-col>
          <b-alert v-if="inactiveSubscription" show class="mb-3" variant="danger">
            <h4>Expired Subscription</h4>
            <p>
              Your subscription has expired. You will not be able to invite new clients until you <b-link to="/subscribe">renew your subscription</b-link>.
            </p>
            <hr />
            <b-button to="/subscriber/manageSubscription" variant="primary">Manage My Subscription</b-button>
          </b-alert>

          <page-title title="Add Client" />
          <hr />

          <b-form novalidate @submit.prevent="onSubmit">
            <text-control label="First Name" class="w-50" v-model="item.firstName" required></text-control>
            <text-control label="Last Name" class="w-50" v-model="item.lastName" required></text-control>

            <h4 class="mt-3">In Custody / Proctored</h4>
            <checkbox-control label="This client is in custody, and I will proctor the questionnaire to them." description="Only questionnaires for proctored clients can be checked out, making them available for offline access." v-model="item.isProctored"></checkbox-control>

            <h4 v-if="!item.isProctored" class="mt-3">Email Invitation</h4>
            <checkbox-control v-if="!item.isProctored" label="Send Email Invitation" v-model="item.useEmail"></checkbox-control>
            <text-control v-if="!item.isProctored || item.email" label="Email Address" class="w-50" v-model="item.email" :custom-validation="validateEmail" type="text" :description="item.isProctored ? 'Optional contact email. No invitation will be sent for a proctored client.' : null"></text-control>

            <div v-if="!item.isProctored">
              <h4 class="mt-3">Text Message Invitation</h4>
              <checkbox-control label="Send Text Message Invitation" v-model="item.useSms"></checkbox-control>
              <text-control label="Phone Number" class="w-50" v-model="item.phoneNumber" :custom-validation="validatePhone" type="tel" placeholder="000-000-0000"></text-control>

              <h4 class="mt-3">Reminder</h4>
              <p>A reminder message will be sent to your client if they do not complete the survey within 3 days.</p>
              <radio-button-list-control label="Reminder Message" class="w-50" :options="reminderDaysOptions" name="Reminder" v-model="item.reminderDays" />

            </div>

            <h4 class="mt-3">Additional Assessments</h4>
            <b-form-checkbox v-for="template in optionalTemplates" :key="template.id" v-model="template.checked" :checked-value="true" :unchecked-value="false">{{template.subscriberTitle}}</b-form-checkbox>

            <h4 class="mt-3">Supporter Documents</h4>
            <p>If enabled, clients can request letters of support from family and friends.</p>
            <checkbox-control label="Allow Client to Request Letters of Support" v-model="item.enableSupporters"></checkbox-control>

            <b-button-toolbar key-nav class="mt-3">
              <b-button-group>
                <b-button type="submit" variant="primary" :disabled="isAdding || !!createdClientId || inactiveSubscription">
                  <b-spinner small v-if="isAdding"></b-spinner>
                  Add Client
                </b-button>
              </b-button-group>
            </b-button-toolbar>
                
          </b-form>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import axios from "axios";
import FormMixin from '../../Mixins/FormMixin.vue';

export default {
  name: "SubscriberClientAdd",
  mixins: [
    FormMixin
  ],
  props: [],
  data() {
    return {
      item: {
        firstName: null,
        lastName: null,
        email: null,
        phoneNumber: null,
        useEmail: true,
        useSms: true,
        reminderDays: 7,
        enableSupporters: true,
        isProctored: false
      },
      templates: [],
      isAdding: false,
      createdClientId: null,
      creationUncertain: false,
      requestId: null,
      subscriptionUser: null,
      inactiveSubscription: false,
      reminderDaysOptions: [
        {
          text: 'Do not send additional reminder messages',
          value: 0
        },
        {
          text: 'Send an additional reminder message after 5 days',
          value: 5
        },
        {
          text: 'Send an additional reminder message after 7 days',
          value: 7
        },
        {
          text: 'Send an additional reminder message after 10 days',
          value: 10
        }
      ]
    };
  },
  computed: {
    optionalTemplates() {
      return this.templates.filter(x => x.isOptional).sort((a, b) => a.initialDisplayWeight - b.initialDisplayWeight);
    }
  },
  methods: {
    validateEmail: function () {
      const email = (this.item.email || '').trim();
      if (!this.item.isProctored && this.item.useEmail && !email)
        return 'Please provide an email address in order to send an email invitation.';

      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
        return 'Please provide a valid email address.';

      return true;
    },
    validatePhone: function () {
      if (this.item.isProctored) return true;
      if (this.item.useSms && !this.item.phoneNumber)
        return 'Please provide a phone number in order to send a text message invitation.';
      
      if (this.item.useSms && !this.item.phoneNumber.match(/^\d{3}-\d{3}-\d{4}$/))
        return 'Please format the phone number as XXX-XXX-XXXX';

      return true;
    },
    loadSubscriptionUser: function () {
      axios({
        url: '/api/subscriptionUser?includes=subscription&filter=' + encodeURIComponent('applicationUserId="' + this.$root.$data.authenticatedUser.id + '"'),
        method: 'GET'
      }).then((response) => {
        if (!response.data || response.data.length < 1) {
          this.subscriptionUser = null;
          this.inactiveSubscription = true;
        } else {
          this.subscriptionUser = response.data[0];
          this.inactiveSubscription = !this.subscriptionUser.subscription.isActive;
        }
      });
    },
    load() {
      axios
        .get('/api/template?limit=100')
        .then(response => {
          console.log(response);

          this.templates = response.data;
          this.templates.forEach(x => x.checked = x.isDefault);
        })
        .catch(error => {
          console.log(error);
        });
    },
    onSubmit(evt) {
      if (this.isAdding || this.createdClientId || this.inactiveSubscription) return;
      if (!this.requestId) {
        const bytes = window.crypto.getRandomValues(new Uint8Array(16));
        bytes[6] = (bytes[6] & 15) | 64;
        bytes[8] = (bytes[8] & 63) | 128;
        const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
        this.requestId = hex.slice(0,8) + '-' + hex.slice(8,12) + '-' + hex.slice(12,16) + '-' + hex.slice(16,20) + '-' + hex.slice(20);
      }
      this.item.requestId = this.requestId;
      // Validate the retained value even if a contact control has been unmounted.
      const emailValid = this.validateEmail() === true;
      if (!this.isFormValid() || !emailValid) {
        this.$parent.showToast('Please make sure you have completed all the fields for the new client.', {
          title: 'Please Provide Additional Information',
          variant: 'danger',
          autoHideDelay: 5000
        });

        return;
      }

      this.isAdding = true;
      
      this.item.templateIds = this.templates.filter(x => x.checked).map(x => x.id);

      axios
        .post('/api/client/invite', { ...this.item, email: (this.item.email || '').trim() || null })
        .then(response => {
          if (!response.data || !response.data.id) {
            this.creationUncertain = true;
            throw new Error('Incomplete creation response');
          }
          this.createdClientId = response.data.id;
          const uncertainDelivery = [response.data.invitationEmailStatus, response.data.invitationSmsStatus]
            .some(status => status !== 'Submitted' && status !== 'NotRequested');
          this.$parent.showToast(uncertainDelivery ?
            'Client saved. Email or SMS delivery failed or could not be confirmed. Do not add this client again; check the existing client.' :
            'Client saved. Requested invitations were submitted to the delivery provider.', {
            title: 'Client Saved',
            variant: uncertainDelivery ? 'warning' : 'success',
            autoHideDelay: uncertainDelivery ? 15000 : 5000
          });

          this.$router.push('/');
        })
        .catch(error => {
          console.log(error);

          let errorMessage = 'Unable to invite ' + this.item.firstName + ' ' + this.item.lastName + '. An unknown error occurred.';

          if (!error.response || error.response.status >= 500 || this.creationUncertain) {
            this.creationUncertain = true;
            errorMessage = "The previous request could not be confirmed. Your entries have been kept. You can retry safely: the same request ID will be used. If the client was already saved, the existing client will be returned without applying subsequent edits or resending invitations.";
          } else if (error.response.status == 400) {
            errorMessage = 'Unable to invite ' + this.item.firstName + ' ' + this.item.lastName + '. ';
            if (error.response.data && typeof error.response.data.Details === 'string')
              errorMessage += error.response.data.Details;
            else
              errorMessage += 'The data you provided could not be validated.';
          } else if (error.response.status == 403)
            errorMessage = 'Unable to invite ' + this.item.firstName + ' ' + this.item.lastName + '. You are not logged in or do not have access.';
          else if (error.response.status == 409)
            errorMessage = 'Unable to save "' + this.item.name + '". The item has been edited since you loaded the page. Reload this page to get the latest changes.';

          this.$parent.showToast(errorMessage, {
            title: 'Error Inviting Client',
            variant: 'danger',
            autoHideDelay: 5000
          });

        })
        .finally(() => { this.isAdding = false; });
    }    
  },
  mounted () {
    this.load();
    this.loadSubscriptionUser();
    // Use this instead of the previous line to test the "Loading" bar
    //window.setTimeout(this.load, 3000);
  }
};
</script>